import { useState, useEffect } from 'react';
import { Heart, Shield, ChevronRight, Check, HandHeart, Book, MessageSquareQuote, HeartHandshake, Loader2 } from 'lucide-react';
import logo from './assets/logo.png';
import { SurahReader } from './components/SurahReader';
import { ZakatPortal } from './components/ZakatPortal';
import { Lifeline } from './components/Lifeline';
import { NavBar } from './components/NavBar';
import { QuranIndex } from './components/QuranIndex';
import { QuranLoader } from './components/QuranLoader';
import { ProfilePage } from './components/ProfilePage';
import { KidsDashboard } from './components/KidsDashboard';
import { Library } from './components/Library';
import { Support } from './components/Support';
import { SunTracker } from './components/SunTracker';
import { NextPrayerCard } from './components/NextPrayerCard';
import { RamadanCard } from './components/RamadanCard';
import { WelcomeScreen } from './components/WelcomeScreen';
import { ArabicLearning } from './components/ArabicLearning';
import { BibleReader } from './components/BibleReader';
import { PrayerGuide } from './components/PrayerGuide';
import { Footprint } from './components/Footprint';
import { Changelog } from './components/Changelog';
import { quranData, dailyPlan } from './data/quran';
import { useLocalization } from './hooks/useLocalization';
import { useFootprint } from './hooks/useFootprint';
import { useProfile } from './hooks/useProfile';
import { useAuth } from './hooks/useAuth';
import { usePrayerAlerts } from './hooks/usePrayerAlerts';
import { subscribeToLikes, broadcastLike } from './services/realtime';

function App() {
  const { session, loading: authLoading } = useAuth();
  usePrayerAlerts(); // Activate background prayer alerts
  
  const [day, setDay] = useState(() => {
    try {
      const saved = localStorage.getItem('habl_day');
      const parsed = saved ? parseInt(saved) : 1;
      return isNaN(parsed) ? 1 : parsed;
    } catch (e) {
      return 1;
    }
  });
  
  const [activeSession, setActiveSession] = useState<'reading' | 'zakat' | 'lifeline' | 'quran' | 'profile' | 'library' | 'support' | 'arabic' | 'bible' | 'prayer' | 'blockchain' | 'changelog' | null>(null);
  const [selectedSurahId, setSelectedSurahId] = useState<number | null>(null);
  const [selectedPrayer, setSelectedPrayer] = useState<string>('Fajr');
  const [isCompleted, setIsCompleted] = useState(false);
  const { lang, setLang, currency, t, isRTL } = useLocalization();
  const { logDeed } = useFootprint();
  const { profile, updateSettings } = useProfile();

  // Global Collective Count (Realtime)
  const [globalLikes, setGlobalLikes] = useState(14532); // Starting baseline
  const [isLiked, setIsLiked] = useState(false);

  useEffect(() => {
    // Subscribe to realtime updates from others
    const unsubscribe = subscribeToLikes((count) => {
      setGlobalLikes(prev => prev + count);
    });
    return () => unsubscribe();
  }, []);

  const handleGlobalLike = () => {
    if (isLiked) return; // Prevent spamming
    setIsLiked(true);
    setGlobalLikes(prev => prev + 1);
    broadcastLike(); // Tell others
    // Reset local lock after 10s so they can like again if they really want to
    setTimeout(() => setIsLiked(false), 10000);
  };

  // Reset completion status when day changes (basic logic)
  useEffect(() => {
    setIsCompleted(false);
  }, [day]);

  // Check for Bible URL on mount
  useEffect(() => {
    if (window.location.pathname === '/bible') {
      setActiveSession('bible');
      // Optional: clean URL
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // Loading State
  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  // Auth Wall
  if (!session) {
    return <WelcomeScreen />;
  }

  if (activeSession === 'profile') {
    return <ProfilePage onBack={() => setActiveSession(null)} />;
  }

  // Kids Mode Interception
  if (profile.settings.kidsMode && activeSession === null) {
    return <KidsDashboard 
        onOpenSession={setActiveSession} 
        profile={profile}
        onExitKidsMode={() => updateSettings({ kidsMode: false })}
        day={day}
    />;
  }

  if (activeSession === 'library') {
    return <Library onBack={() => setActiveSession(null)} />;
  }

  if (activeSession === 'support') {
    return <Support onBack={() => setActiveSession(null)} />;
  }

  if (activeSession === 'arabic') {
    return <ArabicLearning onBack={() => setActiveSession(null)} isKidsMode={profile.settings.kidsMode} />;
  }

  if (activeSession === 'bible') {
    return <BibleReader onBack={() => setActiveSession(null)} />;
  }

  if (activeSession === 'prayer') {
    return <PrayerGuide prayerName={selectedPrayer} onBack={() => setActiveSession(null)} />;
  }

  if (activeSession === 'blockchain') {
    return (
      <div className="min-h-screen bg-slate-900 text-white p-6 pt-safe-top">
        <div className="flex items-center gap-4 mb-8">
          <button 
            onClick={() => setActiveSession(null)}
            className="p-2 -ml-2 rounded-full hover:bg-slate-800 text-slate-400 transition-colors"
          >
            <ChevronRight className="w-6 h-6 rotate-180" />
          </button>
          <h2 className="text-xl font-bold font-mono text-emerald-400">Habl Chain Explorer</h2>
        </div>
        <Footprint defaultShowChain={true} />
        <p className="text-center text-xs text-slate-500 mt-8 font-mono">
          Block Height: #14,532 • Hash Rate: 1.2 PH/s
        </p>
      </div>
    );
  }

  if (activeSession === 'changelog') {
    return <Changelog onBack={() => setActiveSession(null)} />;
  }

  // Handle Full Quran Reader Session
  if (activeSession === 'quran') {
    if (selectedSurahId) {
        return (
            <QuranLoader 
                surahId={selectedSurahId}
                onBack={() => setSelectedSurahId(null)}
                onComplete={() => {
                    logDeed(); // Log deed for reading a Surah
                    // Optional: Auto-advance logic could go here
                }}
            />
        );
    }
    return <QuranIndex onSelectSurah={setSelectedSurahId} onBack={() => setActiveSession(null)} />;
  }

  if (activeSession === 'reading') {
    // Determine which Surah to show based on the Day (Using dailyPlan map)
    const surahId = dailyPlan[(day - 1) % dailyPlan.length]; // Cycle through plan
    const currentSurah = quranData[surahId] || quranData[1];

    return (
      <SurahReader 
        surah={currentSurah} 
        onBack={() => setActiveSession(null)}
        onComplete={() => {
          setIsCompleted(true);
          setActiveSession(null);
          // Auto-progress to next day after a brief delay or immediately
          const nextDay = day + 1;
          setDay(nextDay);
          localStorage.setItem('habl_day', nextDay.toString());
          logDeed(); // Log deed for completing a lesson
        }}
      />
    );
  }

  if (activeSession === 'zakat') {
    return <ZakatPortal onBack={() => setActiveSession(null)} currency={currency} t={t} isRTL={isRTL} />;
  }

  if (activeSession === 'lifeline') {
    return <Lifeline onBack={() => setActiveSession(null)} />;
  }

  // Determine current focus content
  const surahId = dailyPlan[(day - 1) % dailyPlan.length];
  const currentSurah = quranData[surahId] || quranData[1];

  return (
    <div className={`min-h-screen ${profile.settings.darkMode ? 'bg-slate-900 text-white' : 'bg-[#F5F5F4] text-slate-800'} font-sans selection:bg-emerald-200 transition-colors duration-300`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Navigation Bar (Includes Time, Weather, Compass) */}
      <NavBar 
        lang={lang} 
        setLang={setLang} 
        onProfileClick={() => setActiveSession('profile')} 
        profile={profile}
        onToggleKidsMode={() => updateSettings({ kidsMode: !profile.settings.kidsMode })}
      />

      {/* Main Content */}
      <main className="pt-safe-top pb-32 px-6 max-w-md mx-auto flex flex-col gap-8">
        
        {/* Header / Greeting */}
        <header className="space-y-1 text-center">
          <h1 className={`text-3xl font-bold ${profile.settings.darkMode ? 'text-emerald-400' : 'text-emerald-950'}`}>{t.greeting}</h1>
          <p className={`${profile.settings.darkMode ? 'text-slate-400' : 'text-stone-500'} font-medium`}>{t.day} {day} {t.journey}</p>
        </header>

        {/* Sun Tracker Widget */}
        <SunTracker />

        {/* Next Prayer Countdown */}
        <NextPrayerCard onPrayClick={(prayer) => {
            setSelectedPrayer(prayer);
            setActiveSession('prayer');
        }} />

        {/* Ramadan/Taraweh Card */}
        <RamadanCard />

        {/* Daily Focus Card */}
        <div className="bg-white/80 dark:bg-slate-950/50 backdrop-blur-2xl rounded-3xl p-6 shadow-xl shadow-emerald-900/5 border border-white/50 dark:border-white/10 dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1)] relative overflow-hidden group hover:shadow-emerald-900/10 transition-all cursor-pointer">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-full blur-3xl -mr-16 -mt-16 transition-opacity opacity-50 group-hover:opacity-100"></div>
          
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <span className="bg-emerald-100/80 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-200 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider backdrop-blur-sm">{t.focus}</span>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleGlobalLike();
                }}
                className="flex items-center gap-1.5 bg-white/50 dark:bg-slate-700/50 hover:bg-white dark:hover:bg-slate-600 backdrop-blur-md px-2 py-1 rounded-full transition-all group/heart border border-white/20"
              >
                <Heart className={`w-5 h-5 transition-colors ${isLiked ? 'text-red-500 fill-red-500' : 'text-emerald-400 group-hover/heart:text-red-400'}`} />
                <span className="text-[10px] font-bold text-emerald-800 tabular-nums">
                  {globalLikes.toLocaleString()}
                </span>
              </button>
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{currentSurah.englishName}</h2>
            <p className="text-stone-600 dark:text-slate-100 leading-relaxed mb-6">
              Let's learn {currentSurah.name} ({currentSurah.meaning}) together.
              <br/>
              <span className="text-xs font-serif italic opacity-75 dark:opacity-90 dark:text-white mt-1 block">
                In sha Allah with the greatest of intention
              </span>
            </p>
            
            <button 
              onClick={() => setActiveSession('reading')}
              className={`w-full font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors ${
                isCompleted 
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
            >
              {isCompleted ? (
                <> {t.completed} <Check className="w-4 h-4" /></>
              ) : (
                <> {t.start} <ChevronRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} /></>
              )}
            </button>
          </div>
        </div>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div 
            onClick={() => setActiveSession('library')}
            className="bg-white/60 dark:bg-slate-950/40 backdrop-blur-xl p-5 rounded-2xl shadow-sm border border-white/40 dark:border-white/10 dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)] hover:border-emerald-200/50 transition-colors cursor-pointer group"
          >
            <div className="w-10 h-10 bg-stone-50/80 dark:bg-slate-800/50 rounded-full flex items-center justify-center mb-3 group-hover:bg-emerald-50 dark:group-hover:bg-emerald-900/30 transition-colors backdrop-blur-sm">
              <MessageSquareQuote className="w-5 h-5 text-stone-600 dark:text-slate-300 group-hover:text-emerald-600 dark:group-hover:text-emerald-400" />
            </div>
            <h3 className="font-bold text-slate-800 dark:text-white">{t.library}</h3>
            <p className="text-xs text-stone-500 dark:text-slate-200 mt-1">{t.libraryDesc}</p>
          </div>

          <div 
            onClick={() => setActiveSession('arabic')}
            className="bg-white/60 dark:bg-slate-950/40 backdrop-blur-xl p-5 rounded-2xl shadow-sm border border-white/40 dark:border-white/10 dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)] hover:border-emerald-200/50 transition-colors cursor-pointer group"
          >
            <div className="w-10 h-10 bg-stone-50/80 dark:bg-slate-800/50 rounded-full flex items-center justify-center mb-3 group-hover:bg-emerald-50 dark:group-hover:bg-emerald-900/30 transition-colors backdrop-blur-sm">
              <span className="text-xl font-serif text-stone-600 dark:text-slate-300 group-hover:text-emerald-600 dark:group-hover:text-emerald-400">ا</span>
            </div>
            <h3 className="font-bold text-slate-800 dark:text-white">Learn Arabic</h3>
            <p className="text-xs text-stone-500 dark:text-slate-200 mt-1">Master the script</p>
          </div>

          <div 
            onClick={() => setActiveSession('quran')}
            className="bg-white/60 dark:bg-slate-950/40 backdrop-blur-xl p-5 rounded-2xl shadow-sm border border-white/40 dark:border-white/10 dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)] hover:border-emerald-200/50 transition-colors cursor-pointer group"
          >
            <div className="w-10 h-10 bg-stone-50/80 dark:bg-slate-800/50 rounded-full flex items-center justify-center mb-3 group-hover:bg-emerald-50 dark:group-hover:bg-emerald-900/30 transition-colors backdrop-blur-sm">
              <Book className="w-5 h-5 text-stone-600 dark:text-slate-300 group-hover:text-emerald-600 dark:group-hover:text-emerald-400" />
            </div>
            <h3 className="font-bold text-slate-800 dark:text-white">The Quran</h3>
            <p className="text-xs text-stone-500 dark:text-slate-200 mt-1">Read page by page</p>
          </div>

          <div 
            onClick={() => setActiveSession('bible')}
            className="bg-white/60 dark:bg-slate-950/40 backdrop-blur-xl p-5 rounded-2xl shadow-sm border border-white/40 dark:border-white/10 dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)] hover:border-emerald-200/50 transition-colors cursor-pointer group"
          >
            <div className="w-10 h-10 bg-stone-50/80 dark:bg-slate-800/50 rounded-full flex items-center justify-center mb-3 group-hover:bg-amber-50 dark:group-hover:bg-amber-900/30 transition-colors backdrop-blur-sm">
              <Book className="w-5 h-5 text-stone-600 dark:text-slate-300 group-hover:text-amber-600 dark:group-hover:text-amber-400" />
            </div>
            <h3 className="font-bold text-slate-800 dark:text-white">The Bible</h3>
            <p className="text-xs text-stone-500 dark:text-slate-200 mt-1">Shared Wisdom</p>
          </div>

          <div 
            onClick={() => setActiveSession('zakat')}
            className="bg-emerald-50/60 dark:bg-emerald-950/30 backdrop-blur-xl p-5 rounded-2xl shadow-sm border border-emerald-100/50 dark:border-emerald-500/10 dark:shadow-[inset_0_1px_0_0_rgba(16,185,129,0.1)] hover:border-emerald-300/50 transition-colors cursor-pointer group"
          >
            <div className="w-10 h-10 bg-white/60 dark:bg-slate-800/40 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform backdrop-blur-sm">
              <HandHeart className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="font-bold text-emerald-900 dark:text-emerald-100">{t.zakat}</h3>
            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">{t.zakatDesc}</p>
          </div>

          <div 
            onClick={() => setActiveSession('support')}
            className="bg-amber-50/60 dark:bg-amber-950/30 backdrop-blur-xl p-5 rounded-2xl shadow-sm border border-amber-100/50 dark:border-amber-500/10 dark:shadow-[inset_0_1px_0_0_rgba(245,158,11,0.1)] hover:border-amber-300/50 transition-colors cursor-pointer group"
          >
            <div className="w-10 h-10 bg-white/60 dark:bg-slate-800/40 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform backdrop-blur-sm">
              <HeartHandshake className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="font-bold text-amber-900 dark:text-amber-100">Support Us</h3>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Keep the lights on</p>
          </div>
        </div>

        {/* The Panic Button (The Rope) */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white relative overflow-hidden">
           <div className="absolute top-0 right-0 p-4 opacity-30 flex items-center justify-center">
             <Shield className="w-24 h-24 relative z-10" strokeWidth={1.5} />
             <img 
               src={logo} 
               alt="" 
               className="w-10 h-10 absolute opacity-100 drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]" 
             />
           </div>
           <h3 className="font-bold text-lg mb-2 relative z-10">{t.lifeline}</h3>
           <p className="text-slate-300 text-sm mb-4 relative z-10 max-w-[80%]">
             {t.lifelineDesc}
           </p>
           <button 
             onClick={() => setActiveSession('lifeline')}
             className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-sm font-semibold py-2 px-4 rounded-lg border border-white/10 transition-colors relative z-10"
           >
             {t.support}
           </button>
        </div>

        {/* Footer */}
        <footer className="text-center space-y-2 pt-4 pb-2 border-t border-slate-100 dark:border-slate-800">
            <p className="text-xs text-slate-400 dark:text-slate-500">
                Built for the Ummah with ❤️
            </p>
            <div className="flex justify-center gap-4 text-xs font-bold">
                <a href="mailto:salam@habl.life" className="text-emerald-600 dark:text-emerald-400 hover:underline">
                    salam@habl.life
                </a>
                <button 
                    onClick={() => setActiveSession('changelog')}
                    className="text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white transition-colors"
                >
                    Changelog
                </button>
            </div>
            <button 
                onClick={() => setActiveSession('blockchain')}
                className="text-[10px] text-slate-300 dark:text-slate-600 hover:text-emerald-500 dark:hover:text-emerald-400 transition-colors w-full"
            >
                v1.0.2 • Immutable Record on Habl Chain
            </button>
        </footer>

      </main>
    </div>
  );
}

export default App;
